// Prompt 0454 — Tab Organigrama editable (M3).
// Typeahead con datalist nativo. Excluye descendientes del propio puesto.
// Footer Guardar/Cancelar con dirty state.

import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  actualizarOrganigramaPuesto,
  fetchPuestos,
  type PuestoItem,
} from '../../../../../../shared/services/api';

type PuestoItemListado = PuestoItem;
import { useI18n } from '../../../../../../../i18n';

interface OrganigramaTabProps {
  detalle: PuestoItem;
  isDirty: boolean;
  onMarkDirty: () => void;
  onMarkClean: () => void;
  onPuestoUpdated: () => Promise<void> | void;
}

export function OrganigramaTab({
  detalle,
  isDirty,
  onMarkDirty,
  onMarkClean,
  onPuestoUpdated,
}: OrganigramaTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;

  // Padre actual (toma la relacion vigente: fechaFin null + esPrincipal true)
  const padreActualId = useMemo(() => {
    const rel = (detalle.reportaA ?? []).find((r) => !!r.puestoPadre);
    return rel?.puestoPadre.id ?? null;
  }, [detalle]);

  const [puestoPadreIdLocal, setPuestoPadreIdLocal] = useState<string | null>(padreActualId);
  const [listadoPuestos, setListadoPuestos] = useState<PuestoItemListado[]>([]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync padreActualId (si cambia el detalle)
  useEffect(() => {
    setPuestoPadreIdLocal(padreActualId);
  }, [padreActualId]);

  // Cargar listado del tenant (hasta 500)
  useEffect(() => {
    fetchPuestos({ porPagina: '500' })
      .then((r) => {
        const datos = (r as unknown as { datos?: PuestoItemListado[] }).datos;
        const data = (r.data as unknown as PuestoItemListado[]) ?? [];
        setListadoPuestos((datos ?? data) as PuestoItemListado[]);
      })
      .catch(() => setListadoPuestos([]));
  }, []);

  // Calcular descendientes del puesto actual (BFS por reportaA apuntando al actual)
  const descendientesIds = useMemo(() => {
    const ids = new Set<string>();
    const hijosDirectos = (detalle.supervisa ?? []).map((r) => r.puestoHijo?.id).filter(Boolean);
    hijosDirectos.forEach((id) => id && ids.add(id));

    // BFS sobre listadoPuestos resolviendo descendientes transitivos via reportaA
    const queue: string[] = Array.from(ids);
    let iter = 0;
    while (queue.length > 0 && iter < 500) {
      iter += 1;
      const parentId = queue.shift();
      if (!parentId) continue;
      for (const puesto of listadoPuestos) {
        const reporta = puesto.reportaA?.[0]?.puestoPadre?.id;
        if (reporta === parentId && !ids.has(puesto.id) && puesto.id !== detalle.id) {
          ids.add(puesto.id);
          queue.push(puesto.id);
        }
      }
    }
    return ids;
  }, [detalle, listadoPuestos]);

  const candidatos = useMemo(() => {
    const filtro = filtroTexto.trim().toLowerCase();
    return listadoPuestos.filter((p) => {
      if (p.id === detalle.id) return false;
      if (descendientesIds.has(p.id)) return false;
      if (!filtro) return true;
      return (
        p.titulo.toLowerCase().includes(filtro) || (p.codigo ?? '').toLowerCase().includes(filtro)
      );
    });
  }, [listadoPuestos, descendientesIds, detalle.id, filtroTexto]);

  const padreSeleccionadoLabel = useMemo(() => {
    if (!puestoPadreIdLocal) return '';
    const p = listadoPuestos.find((x) => x.id === puestoPadreIdLocal);
    if (!p) return '';
    return p.codigo ? `${p.codigo} — ${p.titulo}` : p.titulo;
  }, [puestoPadreIdLocal, listadoPuestos]);

  const supervisorDerivado = (
    detalle as unknown as { supervisorDerivado?: { nombre: string; apellidos: string } | null }
  ).supervisorDerivado;

  const handleSeleccion = (value: string) => {
    setFiltroTexto(value);
    setError(null);
    // Si coincide exactamente con un label, fijamos el id
    const match = listadoPuestos.find((p) => {
      const label = p.codigo ? `${p.codigo} — ${p.titulo}` : p.titulo;
      return label === value;
    });
    if (match) {
      setPuestoPadreIdLocal(match.id);
      onMarkDirty();
    }
  };

  const handleSinPadre = () => {
    if (puestoPadreIdLocal !== null) {
      setPuestoPadreIdLocal(null);
      setFiltroTexto('');
      onMarkDirty();
    }
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await actualizarOrganigramaPuesto(detalle.id, { puestoPadreId: puestoPadreIdLocal });
      await onPuestoUpdated();
      onMarkClean();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setGuardando(false);
    }
  };

  const handleCancelar = () => {
    setPuestoPadreIdLocal(padreActualId);
    setFiltroTexto('');
    setError(null);
    onMarkClean();
  };

  const hijos = (detalle.supervisa ?? []).map((r) => r.puestoHijo).filter(Boolean);

  return (
    <div>
      {/* Supervisor derivado (lectura) */}
      <section style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>{panel.supervisor ?? 'Supervisor'}</div>
        <div style={{ fontSize: 13, color: '#374151' }}>
          {supervisorDerivado
            ? `${supervisorDerivado.nombre} ${supervisorDerivado.apellidos}`.trim()
            : '—'}
        </div>
      </section>

      {/* Reporta a (edicion) */}
      <section style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>{panel.reportaA ?? 'Reporta a'}</div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input
            type="text"
            list="candidatos-padre"
            value={filtroTexto || padreSeleccionadoLabel}
            onChange={(e) => handleSeleccion(e.target.value)}
            placeholder={panel.placeholderBuscarPadre ?? 'Buscar puesto padre...'}
            style={inputStyle}
          />
          <button type="button" onClick={handleSinPadre} style={btnSinPadre}>
            {panel.sinPadre ?? 'Sin superior'}
          </button>
        </div>
        <datalist id="candidatos-padre">
          {candidatos.map((p) => {
            const label = p.codigo ? `${p.codigo} — ${p.titulo}` : p.titulo;
            return <option key={p.id} value={label} />;
          })}
        </datalist>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
          {padreSeleccionadoLabel
            ? padreSeleccionadoLabel
            : (panel.sinPadreSeleccionado ?? 'Sin superior asignado')}
        </div>
      </section>

      {/* Supervisa (lectura) */}
      <section style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>{panel.supervisa ?? 'Supervisa'}</div>
        {hijos.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94A3B8' }}>
            {panel.sinHijos ?? 'No supervisa puestos'}
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {hijos.map((h) => (
              <li
                key={h.id}
                style={{
                  padding: '8px 12px',
                  background: '#F8FAFC',
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 13,
                  color: '#374151',
                }}
              >
                <strong>{h.titulo}</strong>{' '}
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11,
                    color: '#5A6171',
                  }}
                >
                  {h.codigo ?? ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pie: link a organigrama completo */}
      <a
        href="/structure/organigrama"
        style={{
          display: 'inline-block',
          padding: '6px 14px',
          background: 'transparent',
          color: '#2F5496',
          fontSize: 12,
          textDecoration: 'underline',
        }}
      >
        {panel.editarOrganigrama ?? 'Editar en organigrama'} →
      </a>

      {/* Error inline */}
      {error && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FEF2F2',
            color: '#DC2626',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Footer Guardar / Cancelar */}
      <div style={footerSticky}>
        <button onClick={handleCancelar} disabled={!isDirty} style={btnSecondary(!isDirty)}>
          {panel.cancelarOrganigrama ?? 'Cancelar'}
        </button>
        <button
          onClick={handleGuardar}
          disabled={!isDirty || guardando}
          style={btnPrimary(!isDirty || guardando)}
        >
          {guardando
            ? (panel.saving ?? 'Guardando...')
            : (panel.guardarOrganigrama ?? 'Guardar organigrama')}
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#1F3864',
  marginBottom: 8,
};

const btnSinPadre: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #D1D5DB',
  background: '#fff',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: '#5A6171',
};

const footerSticky: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  marginTop: 20,
  padding: '12px 0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  background: '#fff',
  borderTop: '1px solid #E5E7EB',
};

const btnSecondary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'transparent',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
  color: disabled ? '#9CA3AF' : '#374151',
  opacity: disabled ? 0.6 : 1,
});

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 24px',
  background: disabled ? '#94A3B8' : '#2F5496',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  fontFamily: 'inherit',
});
